// Google Analytics event tracking utility

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

type EventParams = Record<string, string | number | boolean | undefined>;

/**
 * Track a custom event in Google Analytics
 */
export const trackEvent = (eventName: string, params?: EventParams) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, params);
    console.log(`[Analytics] Event: ${eventName}`, params);
  }
};

// ============================================
// USER AUTHENTICATION EVENTS
// ============================================

/** User successfully signed up */
export const trackSignUp = (method: 'email' = 'email', clubId?: string) => {
  trackEvent('sign_up', { method, club_id: clubId });
};

/** User successfully logged in */
export const trackLogin = (method: 'email' | '2fa' = 'email') => {
  trackEvent('login', { method });
};

/** User started login process */
export const trackLoginStart = () => {
  trackEvent('login_start');
};

/** 2FA verification successful */
export const track2FASuccess = (method: 'email' | 'totp') => {
  trackEvent('2fa_verification_success', { method });
};

/** 2FA verification failed */
export const track2FAFailure = (method: 'email' | 'totp') => {
  trackEvent('2fa_verification_failure', { method });
};

/** 2FA setup completed */
export const track2FASetup = (method: 'email' | 'totp') => {
  trackEvent('2fa_setup_complete', { method });
};

/** Password reset requested */
export const trackPasswordResetRequest = () => {
  trackEvent('password_reset_request');
};

/** Password changed successfully */
export const trackPasswordChange = () => {
  trackEvent('password_change_success');
};

// ============================================
// CLUB REGISTRATION EVENTS (Conversion Funnel)
// ============================================

/** Club registration page viewed */
export const trackClubRegistrationView = () => {
  trackEvent('club_registration_view');
};

/** Club registration step completed */
export const trackClubRegistrationStep = (step: number, stepName: string) => {
  trackEvent('club_registration_step', { step, step_name: stepName });
};

/** Club registration completed successfully */
export const trackClubRegistrationComplete = (clubName: string) => {
  trackEvent('club_registration_complete', { club_name: clubName });
};

/** Club registration abandoned */
export const trackClubRegistrationAbandoned = (step: number) => {
  trackEvent('club_registration_abandoned', { abandoned_at_step: step });
};

// ============================================
// CLUB MANAGEMENT EVENTS
// ============================================

/** Club settings updated */
export const trackClubSettingsUpdate = (section: 'general' | 'admin' | 'subscription' | 'sports') => {
  trackEvent('club_settings_update', { section });
};

/** Club logo uploaded */
export const trackClubLogoUpload = () => {
  trackEvent('club_logo_upload');
};

/** Club sports configuration changed */
export const trackClubSportsUpdate = (sports: string[]) => {
  trackEvent('club_sports_update', { sports: sports.join(','), sport_count: sports.length });
};

/** Court configuration updated */
export const trackCourtConfigUpdate = (action: 'add' | 'update' | 'delete', courtType?: string) => {
  trackEvent('court_config_update', { action, court_type: courtType });
};

/** Raster configuration saved */
export const trackRasterConfigSave = (rasterCount: number, totalPositions: number) => {
  trackEvent('raster_config_save', { raster_count: rasterCount, total_positions: totalPositions });
};

// ============================================
// SUBSCRIPTION EVENTS
// ============================================

/** Subscription checkout started */
export const trackSubscriptionStart = (clubId: string) => {
  trackEvent('subscription_checkout_start', { club_id: clubId });
};

/** Subscription activated successfully */
export const trackSubscriptionActivated = () => {
  trackEvent('subscription_activated');
};

/** Subscription cancelled */
export const trackSubscriptionCancelled = () => {
  trackEvent('subscription_cancelled');
};

/** Subscription reactivated */
export const trackSubscriptionReactivated = () => {
  trackEvent('subscription_reactivated');
};

// ============================================
// SESSION MANAGEMENT EVENTS
// ============================================

/** New session created */
export const trackSessionCreate = (sport: string, date: string) => {
  trackEvent('session_create', { sport, session_date: date });
};

/** Session updated */
export const trackSessionUpdate = (sessionId: string) => {
  trackEvent('session_update', { session_id: sessionId });
};

/** Session deleted */
export const trackSessionDelete = () => {
  trackEvent('session_delete');
};

// ============================================
// PLAYER SESSION EVENTS
// ============================================

/** Player signed up for session */
export const trackSessionSignup = (sport: string, hasLimits: boolean) => {
  trackEvent('session_signup', { sport, has_play_limits: hasLimits });
};

/** Player cancelled session signup */
export const trackSessionCancel = () => {
  trackEvent('session_cancel');
};

/** Player stopped after current round */
export const trackSessionStopAfterRound = (roundsPlayed: number) => {
  trackEvent('session_stop_after_round', { rounds_played: roundsPlayed });
};

/** Player re-enrolled in session */
export const trackSessionReEnroll = () => {
  trackEvent('session_re_enroll');
};

/** Player updated play limits */
export const trackPlayLimitsUpdate = (maxRounds?: number, maxTime?: string) => {
  trackEvent('play_limits_update', { 
    max_rounds: maxRounds, 
    max_time: maxTime,
    has_limits: !!(maxRounds || maxTime)
  });
};

// ============================================
// PROFILE EVENTS
// ============================================

/** Profile updated */
export const trackProfileUpdate = (fieldsUpdated: string[]) => {
  trackEvent('profile_update', { fields: fieldsUpdated.join(',') });
};

/** Profile photo uploaded */
export const trackProfilePhotoUpload = () => {
  trackEvent('profile_photo_upload');
};

/** Sport preferences updated */
export const trackSportsPreferenceUpdate = (sports: string[]) => {
  trackEvent('sports_preference_update', { sports: sports.join(','), sport_count: sports.length });
};

/** Skill level changed */
export const trackSkillLevelChange = (oldLevel: number, newLevel: number) => {
  trackEvent('skill_level_change', { old_level: oldLevel, new_level: newLevel });
};

// ============================================
// MEMBER MANAGEMENT EVENTS
// ============================================

/** Member confirmed by admin */
export const trackMemberConfirm = () => {
  trackEvent('member_confirm');
};

/** Member role changed */
export const trackMemberRoleChange = (newRole: string) => {
  trackEvent('member_role_change', { new_role: newRole });
};

// ============================================
// ASSIGNMENT EVENTS
// ============================================

/** Assignments generated automatically */
export const trackAssignmentsGenerated = (playerCount: number, courtCount: number) => {
  trackEvent('assignments_generated', { player_count: playerCount, court_count: courtCount });
};

/** Manual assignment made */
export const trackManualAssignment = () => {
  trackEvent('manual_assignment');
};

// ============================================
// NOTIFICATION EVENTS
// ============================================

/** Push notifications enabled */
export const trackPushNotificationsEnabled = () => {
  trackEvent('push_notifications_enabled');
};

/** Push notifications disabled */
export const trackPushNotificationsDisabled = () => {
  trackEvent('push_notifications_disabled');
};

// ============================================
// PWA EVENTS
// ============================================

/** App installed as PWA */
export const trackPWAInstall = () => {
  trackEvent('pwa_install');
};

/** Install prompt shown */
export const trackPWAInstallPromptShown = () => {
  trackEvent('pwa_install_prompt_shown');
};

/** Install prompt dismissed */
export const trackPWAInstallPromptDismissed = () => {
  trackEvent('pwa_install_prompt_dismissed');
};

// ============================================
// ENGAGEMENT EVENTS
// ============================================

/** Page view (for SPA navigation) */
export const trackPageView = (pagePath: string, pageTitle?: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: pagePath,
      page_title: pageTitle,
      page_location: `https://dehusselapp.nl${pagePath}`,
    });
  }
};

/** Feature used */
export const trackFeatureUsed = (featureName: string) => {
  trackEvent('feature_used', { feature: featureName });
};

/** Error occurred */
export const trackError = (errorType: string, errorMessage?: string) => {
  trackEvent('error', { error_type: errorType, error_message: errorMessage });
};

// ============================================
// DISCOUNT CODE EVENTS
// ============================================

/** Discount code applied successfully */
export const trackDiscountCodeApplied = (code: string, discountType: string, discountValue: number) => {
  trackEvent('discount_code_applied', { code, discount_type: discountType, discount_value: discountValue });
};

/** Discount code validation failed */
export const trackDiscountCodeFailed = (code: string, reason: string) => {
  trackEvent('discount_code_failed', { code, reason });
};

// ============================================
// SUPER ADMIN EVENTS
// ============================================

/** Super admin switched to preview mode */
export const trackPreviewModeEnter = (targetRole: string, targetClubId?: string) => {
  trackEvent('preview_mode_enter', { target_role: targetRole, target_club_id: targetClubId });
};

/** Super admin exited preview mode */
export const trackPreviewModeExit = () => {
  trackEvent('preview_mode_exit');
};

/** Super admin created a new club */
export const trackSuperAdminClubCreate = (clubName: string) => {
  trackEvent('super_admin_club_create', { club_name: clubName });
};

/** Super admin updated a club */
export const trackSuperAdminClubUpdate = (clubId: string) => {
  trackEvent('super_admin_club_update', { club_id: clubId });
};

/** Super admin granted free subscription */
export const trackSuperAdminFreeSubscription = (clubId: string) => {
  trackEvent('super_admin_free_subscription', { club_id: clubId });
};

// ============================================
// CONTACT & ENGAGEMENT EVENTS
// ============================================

/** Contact page viewed */
export const trackContactPageView = () => {
  trackEvent('contact_page_view');
};

/** Contact form submitted successfully */
export const trackContactFormSubmit = () => {
  trackEvent('contact_form_submit');
};

/** Demo request submitted successfully */
export const trackDemoRequestSubmit = (clubName: string) => {
  trackEvent('demo_request_submit', { club_name: clubName });
};

/** Email link clicked */
export const trackEmailLinkClick = (email: string) => {
  trackEvent('email_link_click', { email });
};

/** SEO page viewed */
export const trackSEOPageView = (pageName: string) => {
  trackEvent('seo_page_view', { page_name: pageName });
};

// ============================================
// ACCOUNT EVENTS
// ============================================

/** Account deletion requested */
export const trackAccountDeletionRequest = () => {
  trackEvent('account_deletion_request');
};

/** Account deletion completed */
export const trackAccountDeletionComplete = () => {
  trackEvent('account_deletion_complete');
};

/** Terms accepted */
export const trackTermsAccepted = () => {
  trackEvent('terms_accepted');
};

// ============================================
// ENHANCED ECOMMERCE (GA4)
// ============================================

/** Begin checkout – fired when club registration form is submitted */
export const trackBeginCheckout = (params: {
  clubName: string;
  pricingTier: string;
  priceExclBTW: number;
  coupon?: string;
  discount?: number;
}) => {
  const item = {
    item_id: `subscription_${params.pricingTier}`,
    item_name: `De Hussel App – ${params.pricingTier}`,
    item_category: 'subscription',
    price: params.priceExclBTW,
    quantity: 1,
    ...(params.coupon && { coupon: params.coupon }),
    ...(params.discount && { discount: params.discount }),
  };

  const value = params.discount
    ? Math.max(0, params.priceExclBTW - params.discount)
    : params.priceExclBTW;

  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'begin_checkout', {
      currency: 'EUR',
      value,
      coupon: params.coupon || undefined,
      items: [item],
    });
    console.log('[Analytics] Event: begin_checkout', { value, item });
  }
};

/** Purchase – fired when payment succeeds and club is activated */
export const trackPurchase = (params: {
  transactionId: string;
  clubName: string;
  pricingTier: string;
  priceExclBTW: number;
  coupon?: string;
  discount?: number;
}) => {
  const item = {
    item_id: `subscription_${params.pricingTier}`,
    item_name: `De Hussel App – ${params.pricingTier}`,
    item_category: 'subscription',
    price: params.priceExclBTW,
    quantity: 1,
    ...(params.coupon && { coupon: params.coupon }),
    ...(params.discount && { discount: params.discount }),
  };

  const value = params.discount
    ? Math.max(0, params.priceExclBTW - params.discount)
    : params.priceExclBTW;

  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'purchase', {
      transaction_id: params.transactionId,
      affiliation: params.clubName,
      currency: 'EUR',
      value,
      tax: +(value * 0.21).toFixed(2),
      coupon: params.coupon || undefined,
      items: [item],
    });
    console.log('[Analytics] Event: purchase', { transactionId: params.transactionId, value });
  }
};

/** View item – fired when pricing tier is selected */
export const trackViewItem = (pricingTier: string, priceExclBTW: number) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'view_item', {
      currency: 'EUR',
      value: priceExclBTW,
      items: [{
        item_id: `subscription_${pricingTier}`,
        item_name: `De Hussel App – ${pricingTier}`,
        item_category: 'subscription',
        price: priceExclBTW,
        quantity: 1,
      }],
    });
  }
};
